import { Session } from 'src_common/sdk/session';
import { observable, action, computed, makeAutoObservable } from 'mobx';
import { SdkStaff, StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import { validateAmountPattern, validateAmountRequire } from 'src/domains/players/shared/validatorsNew/validateRequire';
import { validateMinAmount } from 'src/domains/players/shared/validatorsNew/validateMinMaxAmount';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { ApiResponseWrapper } from 'src/utils-mobx/ApiResponseWrapper';
import confirmation from 'src/utils/confirm.jsx';
import { billingInfoFields } from 'src/components/TopUpProcedure/RealexForm/helper';
import { FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { amountViewToValue } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/amountViewToValue';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { PermissionGrantsState } from 'src/AppState/PermissionGrants/PermissionGrants.state';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';

export type ManualAdjustmentMethodType = 'deposits' | 'withdrawals' | null;

export const MANUAL_ADJUSTMET_METHOD_OPTIONS: FilterOptionType<ManualAdjustmentMethodType>[] = [
    {
        label: 'Deposit',
        value: 'deposits',
    },
    {
        label: 'Withdrawal',
        value: 'withdrawals',
    },
];

export type ManualAdjustmentTypeOptionType = 'bank-transfer' | 'cash' | 'cheque' | 'manual' | 'casiono-bonus' | null;

export const MANUAL_ADJUSTMET_TYPE_OPTIONS: FilterOptionType<ManualAdjustmentTypeOptionType>[] = [
    {
        label: 'Bank transfer',
        value: 'bank-transfer',
    },
    {
        label: 'Cash',
        value: 'cash',
    },
    {
        label: 'Cheque',
        value: 'cheque',
    },
    {
        label: 'Manual',
        value: 'manual',
    },
    {
        label: 'Casino Bonus',
        value: 'casiono-bonus',
    },
];

export type ManualAdjustmentTraderOptionType = 'trading' | 'nonTrading' | 'nonTradingCharity' | null;

export const MANUAL_ADJUSTMET_TRADER_OPTIONS: FilterOptionType<ManualAdjustmentTraderOptionType>[] = [
    {
        label: 'Trading',
        value: 'trading',
    },
    {
        label: 'Non-trading',
        value: 'nonTrading',
    },
    {
        label: 'Non-trading Charity',
        value: 'nonTradingCharity',
    },
];

interface AssigneeType {
    id: number;
    name: string;
    type: string;
}

export class ManualAdjustmentFormState {
    public readonly amount: FormInputState<string, Amount>;
    public readonly textArea: FormInputState<string, string>;
    public readonly walletData: () => ApiResponseWrapper<StaffAccountWalletDataType | null>;
    @observable public successMessage: string | null = null;
    @observable public errorMessage: string | null = null;
    @observable public isSubmited: boolean = false;
    @observable public manualAdjustmentMethodValue: ManualAdjustmentMethodType = 'deposits';
    @observable public manualAdjustmentTypeValue: ManualAdjustmentTypeOptionType = 'bank-transfer';
    @observable public manualAdjustmentTraderValue: ManualAdjustmentTraderOptionType = null;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly session: Session,
        private readonly permissionGrantsState: PermissionGrantsState,
        private readonly models: ModelsState,
        private readonly customerId: number,
        private readonly submitCallback: () => void,
        private readonly configState: ConfigState
    ) {
        this.walletData = (): ApiResponseWrapper<StaffAccountWalletDataType | null> =>
            this.models.getUserModel(this.customerId).walletData;
        this.textArea = FormInputState.new('');
        this.amount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validateAmountPattern())
            .map(
                validateMinAmount(
                    new Amount(this.configState.config.minManualAdjustmentAmount),
                    () => moneySymbol(this.getCurrency),
                    'Minimum manual adjustment amount is'
                )
            );
        makeAutoObservable(this);
    }

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const formatValue = amountViewToValue(inputValue);
        this.amount.setValue(formatValue);
    };

    @action public handleSetManualAdjustmentMethodValue = (newValue: ManualAdjustmentMethodType): void => {
        this.manualAdjustmentMethodValue = newValue;
    };

    @action public handleSetManualAdjustmentTypeValue = (newValue: ManualAdjustmentTypeOptionType): void => {
        this.manualAdjustmentTypeValue = newValue;
        if (newValue !== 'manual') {
            this.manualAdjustmentTraderValue = null;
        }
    };

    @action public handleSetManualAdjustmentTraderValue = (newValue: ManualAdjustmentTraderOptionType): void => {
        this.manualAdjustmentTraderValue = newValue;
    };

    @action public handleSetAmount = (newAmount: Amount): void => {
        this.amount.setValue(newAmount.value);
    };

    @action public handleClear = (): void => {
        this.amount.setValue('0');
    };

    @computed public get hasPermission(): boolean {
        return this.permissionGrantsState.has(Permission.bankingWithdrawals) === true;
    }

    @computed public get isTradingMethod(): boolean {
        return this.manualAdjustmentTypeValue === 'manual' || this.manualAdjustmentTypeValue === 'casiono-bonus';
    }

    @computed public get isSelectsNotFilled(): boolean {
        return (
            this.manualAdjustmentMethodValue === null ||
            this.manualAdjustmentTypeValue === null ||
            (this.isTradingMethod && this.manualAdjustmentTraderValue === null)
        );
    }

    @computed public get isButtonDisabled(): boolean {
        console.log(this.amount.result.value);

        return this.amount.result.value.type === 'error' || this.isSelectsNotFilled || this.hasPermission === false;
    }

    @computed public get getCurrency(): CurrencyType {
        return this.walletData().data?.currency ?? 'XTS';
    }

    @computed public get getBalance(): Amount {
        return new Amount(this.walletData().data?.balance ?? '0');
    }

    @computed public get getWithdrawableBalance(): Amount {
        return new Amount(this.walletData().data?.withdrawableBalance ?? '0');
    }

    @computed public get getBackofficeWithdrawableBalance(): Amount {
        return new Amount(this.walletData().data?.backofficeWithdrawableBalance ?? '0');
    }

    @computed public get getFailedWithdrawalsFunds(): Amount {
        return this.getBackofficeWithdrawableBalance.sub(this.getWithdrawableBalance);
    }

    @computed public get newBalance(): Amount {
        try {
            const basicInput = this.amount.result.value.type === 'ok' ? this.amount.result.value.data : new Amount('0');

            if (this.manualAdjustmentMethodValue === 'withdrawals') {
                return this.getBalance.sub(basicInput);
            }

            if (this.manualAdjustmentMethodValue === 'deposits') {
                return this.getBalance.add(basicInput);
            }

            return this.getBalance;
        } catch (error) {
            return new Amount('0');
        }
    }

    private getAssignee = (): AssigneeType | null => {
        if (this.session.userId === null || this.session.loggedUserName === null) {
            return null;
        }

        return {
            name: this.session.loggedUserName,
            id: this.session.userId,
            type: 'account',
        };
    };

    @action public resetState = (): void => {
        //dropdown fields should not reset values
        this.amount.reset();
        this.textArea.reset();
        this.errorMessage = null;
        this.isSubmited = false;
    };

    public depositInitiateId = async (amount: Amount, displayedReason: string | null): Promise<number | null> => {
        const basicData = this.models.getUserModel(this.customerId).basicData;

        if (basicData.data === null) {
            this.errorMessage = 'Missing user basic data';
            this.createErrorMessage('missing-user-basc-data');
            return null;
        }

        const billingInfo = billingInfoFields(basicData.data);

        if (billingInfo.type === 'error') {
            this.createErrorMessage('missing-fields', billingInfo.fieldsRequired.join(', '));
            return null;
        }

        try {
            const response = await this.sdkStaff.depositInitiate({
                customer_id: this.customerId,
                requestBody: {
                    channel: 'backoffice',
                    amount: $appState.configState.precision.valueOldFormat(amount),
                    billingInfo: billingInfo.data,
                    displayedReason: displayedReason,
                    status: 'paid',
                    tags: {
                        adjustmentType: this.manualAdjustmentTypeValue,
                        accountAdjustment:
                            this.manualAdjustmentTypeValue !== 'manual' &&
                            this.manualAdjustmentTypeValue !== 'casiono-bonus'
                                ? false
                                : true,
                        tradingAdjustment: this.manualAdjustmentTraderValue === 'trading',
                    },
                },
            });

            if (response.status === '422') {
                const { errors } = response;
                const error = errors[0];
                if (error?.code === 'minimum') {
                    this.createErrorMessage('minimum');
                } else if (error?.code === 'limit-reached') {
                    this.createErrorMessage('limit-reached');
                } else if (error?.code === 'deposits-not-allowed') {
                    this.createErrorMessage('deposits-not-allowed', error.debugDetails ?? undefined);
                } else if (error?.field === 'depositRestriction') {
                    this.createErrorMessage('deposit-restriction');
                } else {
                    this.createErrorMessage('unknown');
                }
                return null;
            }
            return response.bodyJson.id;
        } catch (e) {
            this.createErrorMessage('unknown');
            return null;
        }
    };

    public withdrawInitiateId = async (amount: Amount, displayedReason: string | null): Promise<number | null> => {
        try {
            const response = await this.sdkStaff.withdraw({
                customer_id: this.customerId,
                requestBody: {
                    amount: $appState.configState.precision.valueOldFormat(amount),
                    channel: 'backoffice',
                    assignee: this.getAssignee(),
                    displayed_reason: displayedReason,
                    status: 'paid',
                    tags: {
                        adjustmentType: this.manualAdjustmentTypeValue,
                        accountAdjustment: true,
                        tradingAdjustment: this.manualAdjustmentTraderValue === 'trading',
                    },
                },
            });

            if (response.status === '201') {
                return response.data.id;
            }

            this.createErrorMessage('custom', response.errors[0]?.debugDetails);

            return null;
        } catch (e) {
            this.createErrorMessage('unknown');
            return null;
        }
    };

    @action public handleSubmit = async (): Promise<void> => {
        this.isSubmited = true;
        this.successMessage = null;
        this.errorMessage = null;

        if (this.hasPermission === false) {
            return;
        }

        this.amount.setAsVisited();
        if (this.amount.result.value.type === 'error' || this.isSelectsNotFilled) {
            return;
        }

        try {
            this.successMessage = null;
            this.errorMessage = null;
            const amount = this.amount.result.value.data;
            const displayedReason = this.textArea.value === '' ? null : this.textArea.value;
            const confirmationMethodText = this.manualAdjustmentMethodValue === 'deposits' ? 'deposit' : 'withdraw';
            const result = await confirmation(
                `Are you sure that you want to ${confirmationMethodText} ${amount.format(this.getCurrency)} ?`,
                true
            );

            if (this.shouldValidateWithdrawalLimit && amount.isGreaterThan(this.withdrawalStandardLimit)) {
                this.createErrorMessage('no-permission-to-withdraw-over-limit');
                return;
            }

            //@ts-expect-error
            if (result === true && this.manualAdjustmentMethodValue !== null) {
                const methodResponseId =
                    this.manualAdjustmentMethodValue === 'deposits'
                        ? await this.depositInitiateId(amount, displayedReason)
                        : await this.withdrawInitiateId(amount, displayedReason);

                if (methodResponseId === null) {
                    return;
                }

                const response = await this.sdkStaff.manual_adjustment_confirm({
                    customer_id: this.customerId,
                    manual_adjustment_id: methodResponseId,
                    manual_adjustment_type: this.manualAdjustmentMethodValue,
                });

                if (response.status === '200') {
                    this.submitCallback();
                    this.resetState();
                    this.successMessage = 'Account has been adjusted successfully.';
                    return;
                }

                this.createErrorMessage('custom', response.data.errors[0]?.debugDetails ?? undefined);
                return;
            }
        } catch (e) {
            this.errorMessage = 'Unknown error occurred';
        }
    };

    @computed private get withdrawalStandardLimit(): Amount {
        return new Amount(this.configState.config.withdrawalStandardLimit);
    }

    @computed private get shouldValidateWithdrawalLimit(): boolean {
        return (
            this.manualAdjustmentMethodValue === 'withdrawals' &&
            this.permissionGrantsState.has(Permission.bankingWithdrawalsNoLimit) === false
        );
    }

    private createErrorMessage = (errorType: string, message?: string): void => {
        switch (errorType) {
            case 'invalid-data':
                this.errorMessage = 'Some fields are invalid.';
                break;
            case 'minimum':
                this.errorMessage = `Deposit amount can not be lower than defined ${this.configState.config.minDepositAmount} limit`;
                break;
            case 'limit-reached':
                this.errorMessage = "Deposit can't exceed defined limit.";
                break;
            case 'deposits-not-allowed':
                this.errorMessage = message === undefined ? 'Your account is suspended.' : message;
                break;
            case 'declined':
                this.errorMessage = 'ERROR_DEPOSIT_DECLINED';
                break;
            case 'missing-user-data':
                this.errorMessage = 'Missing user basic data';
                break;
            case 'missing-fields':
                if (message === undefined) {
                    this.errorMessage = 'Unknown error happened';
                    return;
                }
                this.errorMessage = `Please add customer ${message} to customer 'Personal account' details.`;
                break;
            case 'errorIframe':
                this.errorMessage = 'Error in transaction final step. More details in Payment Search';
                break;
            case 'deposit-restriction':
                this.errorMessage = 'Deposit is not allowed when account is deposit restricted';
                break;
            case 'no-permission-to-withdraw-over-limit':
                this.errorMessage = `You don't have permission to perform withdrawals over ${this.withdrawalStandardLimit.format('GBP')}`;
                break;
            case 'unknown':
                this.errorMessage = 'Unknown error';
                break;
            case 'custom':
                if (message === undefined) {
                    this.errorMessage = 'Unknown error happened';
                    return;
                }

                this.errorMessage = message;
                break;
            default:
                this.errorMessage = 'Unknown error happened';
        }
    };
}
